import { ScaleIcon } from "lucide-react";

import {
	Combobox,
	ComboboxContent,
	ComboboxEmpty,
	ComboboxInput,
	ComboboxItem,
	ComboboxList,
} from "@/components/ui/combobox";
import type { Id } from "../../../../convex/_generated/dataModel";
import type { ClioAccount } from "./types";

interface AccountSwitcherProps {
	accounts: ClioAccount[];
	selectedConnectionId: Id<"oauthConnections">;
	onSelect: (connectionId: Id<"oauthConnections">) => void;
}

export function AccountSwitcher({
	accounts,
	selectedConnectionId,
	onSelect,
}: AccountSwitcherProps) {
	const selected =
		accounts.find((a) => a.connectionId === selectedConnectionId) ??
		accounts[0];

	return (
		<Combobox
			items={accounts}
			value={selected}
			onValueChange={(next: ClioAccount | null) => {
				if (next) onSelect(next.connectionId);
			}}
			itemToStringLabel={(acc: ClioAccount) =>
				acc.accountEmail || acc.ownerName || "Clio account"
			}
			itemToStringValue={(acc: ClioAccount) => acc.connectionId}
		>
			<ComboboxInput
				aria-label="Select Clio account"
				placeholder="Select Clio account"
				className="w-full [&_input]:text-ellipsis"
			/>
			<ComboboxContent>
				<ComboboxEmpty>No Clio accounts found.</ComboboxEmpty>
				<ComboboxList>
					{(acc: ClioAccount) => (
						<ComboboxItem key={acc.connectionId} value={acc}>
							<div className="flex min-w-0 flex-1 items-center gap-2">
								<ScaleIcon className="h-4 w-4 shrink-0 text-primary" />
								<div className="flex min-w-0 flex-col">
									<span className="truncate text-sm">{acc.accountEmail}</span>
									<span className="truncate text-xs text-muted-foreground">
										{acc.ownerName}
									</span>
								</div>
							</div>
						</ComboboxItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
}
